import { Component, Input, Output, EventEmitter, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-angular';

@Component({
  selector: 'app-od-pagination',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  template: `
    <div class="flex items-center justify-between p-6 rounded bg-white font-roboto text-sm min-h-[48px] mt-4 max-sm:flex-col max-sm:gap-3 max-sm:p-4">
      <div class="text-[#2874c4] font-normal whitespace-nowrap max-sm:text-xs">
        {{ startItem }}-{{ endItem }} de {{ totalItems }} resultados
      </div>
      
      <div class="flex items-center gap-1">
        <button 
          class="flex items-center justify-center w-8 h-8 bg-transparent border border-transparent rounded cursor-pointer transition-all duration-200 text-[#4285f4] p-0 hover:bg-[#f0f8ff] disabled:cursor-not-allowed disabled:opacity-40 disabled:text-[#c0c0c0]"
          [disabled]="currentPage() === 1"
          (click)="goToFirstPage()"
          aria-label="Primera página">
          <lucide-icon [name]="chevronsLeft" [size]="16"></lucide-icon>
        </button>
        
        <button 
          class="flex items-center justify-center w-8 h-8 bg-transparent border border-transparent rounded cursor-pointer transition-all duration-200 text-[#4285f4] p-0 hover:bg-[#f0f8ff] disabled:cursor-not-allowed disabled:opacity-40 disabled:text-[#c0c0c0]"
          [disabled]="currentPage() === 1"
          (click)="goToPreviousPage()"
          aria-label="Página anterior">
          <lucide-icon [name]="chevronLeft" [size]="16"></lucide-icon>
        </button>
        
        <!-- Page numbers -->
        @for (pageNum of visiblePages(); track pageNum) {
          <button 
            class="flex items-center  justify-center  border-0 outline-0 size-[24px] rounded cursor-pointer transition-all duration-200 font-medium text-sm mx-0.5"
            [ngClass]="{
              'text-[#F8BB04] bg-white': pageNum === currentPage(),
              'bg-transparent text-[#666666] hover:text-[#333333]': pageNum !== currentPage()
            }"
            (click)="goToPage(pageNum)"
            [attr.aria-label]="'Página ' + pageNum"
            [attr.aria-current]="pageNum === currentPage() ? 'page' : null">
            {{ pageNum }}
          </button>
        }
      
        <button 
          class="flex items-center justify-center w-8 h-8 bg-transparent border border-transparent rounded cursor-pointer transition-all duration-200 text-[#4285f4] p-0 hover:bg-[#f0f8ff] disabled:cursor-not-allowed disabled:opacity-40 disabled:text-[#c0c0c0]"
          [disabled]="currentPage() === totalPages()"
          (click)="goToNextPage()"
          aria-label="Página siguiente">
          <lucide-icon [name]="chevronRight" [size]="16"></lucide-icon>
        </button>
        
        <button 
          class="flex items-center justify-center w-8 h-8 bg-transparent border border-transparent rounded cursor-pointer transition-all duration-200 text-[#4285f4] p-0 hover:bg-[#f0f8ff] disabled:cursor-not-allowed disabled:opacity-40 disabled:text-[#c0c0c0]"
          [disabled]="currentPage() === totalPages()"
          (click)="goToLastPage()"
          aria-label="Última página">
          <lucide-icon [name]="chevronsRight" [size]="16"></lucide-icon>
        </button>
      </div>
    </div>
  `,
  styles: [`
    :host ::ng-deep lucide-icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `]
})
export class OdPaginationComponent {
  // Icons
  readonly chevronLeft = ChevronLeft;
  readonly chevronRight = ChevronRight;
  readonly chevronsLeft = ChevronsLeft;
  readonly chevronsRight = ChevronsRight;

  // Input properties
  @Input() totalItems = 0;
  @Input() itemsPerPage = 10;
  @Input() set page(value: number) {
    this.currentPage.set(value);
  }

  // Output events
  @Output() pageChange = new EventEmitter<number>();

  // Internal state
  currentPage = signal(1);

  // Computed properties
  totalPages = computed(() => Math.ceil(this.totalItems / this.itemsPerPage) || 1);
  
  // Calculate visible page numbers (show at least 5 when possible)
  visiblePages = computed(() => {
    const total = this.totalPages();
    const current = this.currentPage();
    const pages: number[] = [];
    
    // If total pages is 5 or less, show all
    if (total <= 5) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
      return pages;
    }
    
    // For more than 5 pages, calculate range
    let start: number;
    let end: number;
    
    if (current <= 3) {
      // Near the beginning
      start = 1;
      end = 5;
    } else if (current >= total - 2) {
      // Near the end
      start = total - 4;
      end = total;
    } else {
      // In the middle
      start = current - 2;
      end = current + 2;
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return pages;
  });
  
  get startItem(): number {
    return Math.min((this.currentPage() - 1) * this.itemsPerPage + 1, this.totalItems);
  }

  get endItem(): number {
    return Math.min(this.currentPage() * this.itemsPerPage, this.totalItems);
  }

  // Navigation methods
  goToFirstPage(): void {
    if (this.currentPage() !== 1) {
      this.currentPage.set(1);
      this.pageChange.emit(1);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage() > 1) {
      const newPage = this.currentPage() - 1;
      this.currentPage.set(newPage);
      this.pageChange.emit(newPage);
    }
  }

  goToNextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      const newPage = this.currentPage() + 1;
      this.currentPage.set(newPage);
      this.pageChange.emit(newPage);
    }
  }

  goToLastPage(): void {
    const lastPage = this.totalPages();
    if (this.currentPage() !== lastPage) {
      this.currentPage.set(lastPage);
      this.pageChange.emit(lastPage);
    }
  }

  goToPage(page: number): void {
    if (page !== this.currentPage() && page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.pageChange.emit(page);
    }
  }
}